// BULLETPROOF PaymentReturnScreen.js - Guaranteed single poll
// Replace the entire file with this version

import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Stack,
    Text,
    Button,
    Loader,
    Alert,
    Card,
    Group,
    Badge,
    Center,
    Title
} from '@mantine/core';
import { IconCheck, IconX, IconClock, IconFileText } from '@tabler/icons-react';
import { checkPaymentByOrder } from '../misc/api';

export function PaymentReturnScreen() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const orderRef = searchParams.get('order');

    const [loading, setLoading] = useState(true);
    const [payment, setPayment] = useState(null);
    const [error, setError] = useState(null);
    const [checkCount, setCheckCount] = useState(0);

    // Track if component is mounted
    const isMountedRef = useRef(true);
    const hasSetupPollingRef = useRef(false);

    const MAX_CHECKS = 40;

    useEffect(() => {
        // Prevent double-setup in React Strict Mode
        if (hasSetupPollingRef.current) {
            console.log('Polling already setup, skipping...');
            return;
        }

        hasSetupPollingRef.current = true;
        isMountedRef.current = true;

        const order = orderRef || localStorage.getItem('pending_order');

        if (!order) {
            setError('No order reference found');
            setLoading(false);
            return;
        }

        let pollCount = 0;
        let intervalId = null;
        let isPolling = true;

        const checkStatus = async () => {
            // Stop if unmounted or exceeded max
            if (!isMountedRef.current) {
                console.log('Component unmounted, stopping check');
                return;
            }

            if (pollCount >= MAX_CHECKS) {
                console.log('Max checks reached');
                if (intervalId) clearInterval(intervalId);
                if (isMountedRef.current) {
                    setError('Payment check timeout. Please refresh or contact support.');
                }
                return;
            }

            try {
                pollCount++;
                console.log(`Check #${pollCount}/${MAX_CHECKS}`);

                const data = await checkPaymentByOrder(order);

                if (!isMountedRef.current) return;

                if (data.success) {
                    setPayment(data.data);
                    setCheckCount(pollCount);
                    setLoading(false);

                    // Stop polling if payment is finalized
                    if (data.data.status === 'paid' || data.data.status === 'failed') {
                        console.log(`âœ“ Payment finalized: ${data.data.status} - STOPPING POLL`);
                        isPolling = false;

                        if (intervalId) {
                            clearInterval(intervalId);
                            intervalId = null;
                        }

                        localStorage.removeItem('pending_order');
                        localStorage.removeItem('pending_ppr');
                    } else {
                        console.log(`Payment still pending, will check again in 3s...`);
                    }
                } else {
                    if (isMountedRef.current) {
                        setError(data.message || 'Failed to fetch payment status');
                        setLoading(false);
                    }
                    isPolling = false;
                    if (intervalId) clearInterval(intervalId);
                }
            } catch (err) {
                console.error('Error checking payment:', err);
                if (isMountedRef.current) {
                    setError('Failed to check payment status');
                    setLoading(false);
                }
                isPolling = false;
                if (intervalId) clearInterval(intervalId);
            }
        };

        // Initial check
        console.log('Starting payment status checks...');
        checkStatus();

        // Setup polling interval
        intervalId = setInterval(() => {
            if (!isPolling) {
                console.log('Polling flag is false, clearing interval');
                if (intervalId) clearInterval(intervalId);
                return;
            }
            checkStatus();
        }, 3000);

        // Cleanup
        return () => {
            console.log('Cleanup: stopping all polling');
            isMountedRef.current = false;
            isPolling = false;
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };
    }, [orderRef]); // Only re-run if orderRef changes

    if (loading && !payment) {
        return (
            <Center style={{ minHeight: '60vh' }}>
                <Stack align="center" spacing="md">
                    <Loader size="lg" />
                    <Text>Checking payment status...</Text>
                </Stack>
            </Center>
        );
    }

    if (error && !payment) {
        return (
            <Center style={{ minHeight: '60vh' }}>
                <Stack spacing="md" style={{ maxWidth: 600, width: '100%', padding: 20 }}>
                    <Alert icon={<IconX />} color="red" title="Error">
                        {error}
                    </Alert>
                    <Group position="center">
                        <Button onClick={() => window.location.reload()}>Retry</Button>
                        <Button variant="outline" onClick={() => navigate('/')}>Back to Home</Button>
                    </Group>
                </Stack>
            </Center>
        );
    }

    // Payment successful
    if (payment?.status === 'paid') {
        return (
            <Center style={{ minHeight: '60vh' }}>
                <Stack spacing="lg" style={{ maxWidth: 700, width: '100%', padding: 20 }}>
                    <Stack align="center" spacing="md">
                        <IconCheck size={80} color="green" strokeWidth={2} />
                        <Title order={2} color="green">
                            Payment Successful!
                        </Title>
                    </Stack>

                    {/* PPR Number */}
                    {payment.ppr_number && (
                        <Card withBorder padding="xl" style={{ backgroundColor: '#f0f9ff' }}>
                            <Stack spacing="xs" align="center">
                                <Group spacing="xs">
                                    <IconFileText size={24} color="#2563eb" />
                                    <Text size="sm" color="dimmed" weight={500}>
                                        Your PPR Number, valid till {
                                            new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                                                .toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric'
                                                })
                                        }
                                    </Text>
                                </Group>
                                <Text
                                    weight={700}
                                    color="#2563eb"
                                    style={{ letterSpacing: '0.1em' }}
                                >
                                    {payment.ppr_number}
                                </Text>
                                {payment.ppr_type && (
                                    <Badge size="lg" variant="filled" color="blue">
                                        {payment.ppr_type === 'one_time' ? 'One-Time PPR' : 'Subscription PPR'}
                                    </Badge>
                                )}
                            </Stack>
                        </Card>
                    )}

                    {/* Payment Details */}
                    <Card withBorder padding="lg">
                        <Stack spacing="md">
                            <Text size="lg" weight={600}>Payment Details</Text>

                            <Group position="apart">
                                <Text color="dimmed">Order Reference</Text>
                                <Text size="sm" weight={500} style={{ fontFamily: 'monospace' }}>
                                    {payment.order_reference}
                                </Text>
                            </Group>

                            <Group position="apart">
                                <Text color="dimmed">Amount Paid</Text>
                                <Text size="lg" weight={600} color="green">
                                    € {parseFloat(payment.amount).toFixed(2)}
                                </Text>
                            </Group>

                            <Group position="apart">
                                <Text color="dimmed">Payment Status</Text>
                                <Badge size="lg" color="green" variant="filled">
                                    Confirmed
                                </Badge>
                            </Group>

                            {payment.flight_type && (
                                <Group position="apart">
                                    <Text color="dimmed">Flight Type</Text>
                                    <Badge variant="outline">
                                        {payment.flight_type.replace('_', ' ').toUpperCase()}
                                    </Badge>
                                </Group>
                            )}
                        </Stack>
                    </Card>

                    <Alert icon={<IconCheck />} color="green" title="What's Next?">
                        <Text size="sm">
                            Your PPR confirmation has been sent to your email.
                            Please save your PPR number <strong>{payment.ppr_number}</strong> for your flight.
                        </Text>
                    </Alert>

                    {/* <Group position="center" spacing="md">
                        <Button 
                            size="lg"
                            onClick={() => navigate('/flight-history')}
                        >
                            View My Flights
                        </Button>
                        <Button 
                            size="lg"
                            variant="outline" 
                            onClick={() => navigate('/')}
                        >
                            Back to Home
                        </Button>
                    </Group> */}
                </Stack>
            </Center>
        );
    }

    // Payment failed
    if (payment?.status === 'failed') {
        return (
            <Center style={{ minHeight: '60vh' }}>
                <Stack spacing="md" style={{ maxWidth: 600, width: '100%', padding: 20 }}>
                    <Stack align="center" spacing="md">
                        <IconX size={64} color="red" />
                        <Text size="xl" weight={700} color="red">
                            Payment Failed
                        </Text>
                    </Stack>

                    <Alert icon={<IconX />} color="red">
                        Your payment could not be processed. Please try again or contact support.
                    </Alert>

                    <Card withBorder>
                        <Stack spacing="xs">
                            <Text size="sm" color="dimmed">
                                Order Reference: {payment.order_reference}
                            </Text>
                            {payment.payment_state && (
                                <Text size="sm" color="dimmed">
                                    Payment State: {payment.payment_state}
                                </Text>
                            )}
                        </Stack>
                    </Card>

                    <Group position="center">
                        <Button onClick={() => navigate('/register-single')}>
                            Try Again
                        </Button>
                        <Button variant="outline" onClick={() => navigate('/')}>
                            Back to Home
                        </Button>
                    </Group>
                </Stack>
            </Center>
        );
    }

    // Payment pending
    return (
        <Center style={{ minHeight: '60vh' }}>
            <Stack spacing="md" style={{ maxWidth: 600, width: '100%', padding: 20 }}>
                <Stack align="center" spacing="md">
                    <Loader size="lg" />
                    <IconClock size={48} color="orange" />
                    <Text size="xl" weight={700} color="orange">
                        Payment Processing
                    </Text>
                </Stack>

                <Alert icon={<IconClock />} color="orange">
                    Your payment is being processed. This usually takes a few seconds.
                    Please wait...
                </Alert>

                <Card withBorder>
                    <Stack spacing="xs">
                        <Text size="sm" color="dimmed">
                            Order Reference: {payment?.order_reference}
                        </Text>
                        <Text size="xs" color="dimmed">
                            Check #{checkCount} of {MAX_CHECKS}
                        </Text>
                    </Stack>
                </Card>

                {checkCount > 20 && (
                    <Alert color="yellow">
                        This is taking longer than expected. You can safely leave this page -
                        we'll email you once your payment is processed.
                    </Alert>
                )}
            </Stack>
        </Center>
    );
}